import { useRef, useState, useCallback } from "react";
import moment from "moment";
import { WEEK_DAYS } from "../Tarrif/TarifUtils";

// Secure unique ID generator: prefers window.crypto; falls back to timestamp+counter.
let __uidCounter = 0;
export function secureUniqueId(prefix = "uid") {
  try {
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${prefix}-${hex}`;
    }
  } catch (e) {
    // ignore and use fallback
  }
  const ts = Date.now().toString(36);
  __uidCounter = (__uidCounter + 1) % 1_000_000;
  return `${prefix}-${ts}-${__uidCounter.toString(36)}`;
}

/**
 * Sort time slots by start time
 */
export const sortTimeSlots = (timeSlots) => {
  return [...timeSlots].sort((a, b) => {
    const startA = moment({
      hour: a.startTime.hour,
      minute: a.startTime.minute,
    });
    const startB = moment({
      hour: b.startTime.hour,
      minute: b.startTime.minute,
    });
    return startA.diff(startB);
  });
};

/**
 * Check if time slots cover the full 24 hours (0:00 to 24:00)
 */
export const validateTimeSlotsCoverage = (timeSlots) => {
  if (timeSlots.length === 0) return { valid: false, error: "empty" };

  const sortedSlots = sortTimeSlots(timeSlots);
  const firstTimeSlot = sortedSlots[0];
  const lastTimeSlot = sortedSlots[sortedSlots.length - 1];

  const dayStart = moment({ hour: 0, minute: 0 });
  const dayEnd = moment({ hour: 24, minute: 0 });

  const firstStartTime = moment({
    hour: firstTimeSlot.startTime.hour,
    minute: firstTimeSlot.startTime.minute,
  });

  const lastEndTime = moment({
    hour: lastTimeSlot.endTime.hour,
    minute: lastTimeSlot.endTime.minute,
  });

  if (!firstStartTime.isSame(dayStart)) {
    return { valid: false, error: "incomplete24h" };
  }

  if (!lastEndTime.isSame(dayEnd)) {
    return { valid: false, error: "incomplete24h" };
  }

  return { valid: true };
};

/**
 * Check if time slots are contiguous (no gaps, no overlaps)
 */
export const validateTimeSlotsContiguous = (timeSlots) => {
  if (timeSlots.length <= 1) return { valid: true };

  const sortedSlots = sortTimeSlots(timeSlots);

  for (let k = 0; k < sortedSlots.length - 1; k++) {
    const currentEndTime = moment({
      hour: sortedSlots[k].endTime.hour,
      minute: sortedSlots[k].endTime.minute,
    });
    const nextStartTime = moment({
      hour: sortedSlots[k + 1].startTime.hour,
      minute: sortedSlots[k + 1].startTime.minute,
    });

    if (!currentEndTime.isSame(nextStartTime)) {
      return { valid: false, error: "notContiguous" };
    }
  }

  return { valid: true };
};

/**
 * Validate that all weekdays are covered
 */
export const validateWeekdaysCoverage = (periods) => {
  const allWeekdays = new Set(WEEK_DAYS.map((day) => day.id));
  const seasonWeekdays = new Set();

  periods.forEach((period) => {
    (period.weekDays || []).forEach((day) => {
      seasonWeekdays.add(day);
    });
  });

  return seasonWeekdays.size === allWeekdays.size;
};

/**
 * Custom hook for tariff/demand charge snackbar management
 */
export const useTariffSnackbar = (defaultMessage = "Success") => {
  const snackbarId = useRef(secureUniqueId("snackbar-custom"));
  const [snackbarProps, setSnackbarProps] = useState({
    open: false,
    type: "success",
    canClose: true,
    message: defaultMessage,
    icon: "information_circle",
    button: "Close",
    duration: 5000,
  });

  const callSnackbar = useCallback((type, message) => {
    setSnackbarProps({
      type,
      message,
    });
    const element = document.getElementById(snackbarId.current);
    if (element) {
      element.open = true;
    }
  }, []);

  return {
    snackbarId: snackbarId.current,
    snackbarProps,
    callSnackbar,
  };
};

/**
 * Snackbar component for tariff/demand charge
 */
export const TariffSnackbar = ({ id, type, message }) => (
  <se-snackbar
    id={id}
    type={type}
    icon="information_stroke"
    message={message}
    duration="3000"
  ></se-snackbar>
);
